"use client";

import React, { Suspense, useContext } from "react";

import { LazyEncapsulatedQuickAdd } from "@faire/retailer/components/QuickAdd/LazyEncapsulatedQuickAdd";
import { QuickAddProps } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddProps";
import { QuickAddTrackingEventsContext } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddTrackingEventsContext/QuickAddTrackingEventsContext";
import { QuickAddTrackingEventsContextProvider } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddTrackingEventsContext/QuickAddTrackingEventsContextProvider";
import { useProductTileContainerRef } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileContainerRefContextProvider";
import { canQuickAdd } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/useCanQuickAdd";
import { useCartUpdate } from "@faire/retailer/hooks/cart/useCartUpdate";
import { useSettingPersistQuickAddOnSearchTile } from "@faire/retailer/settings/useSettingPersistQuickAddOnSearchTile";

import { ComponentDisplayOnHoverContainer } from "../../ComponentDisplayOnHoverContainer";
import { useContainerRef } from "../../useContainerRef";

const QuickAddBase = ({
  data: { productToken, quickAdd, brandToken, state },
}: {
  data: QuickAddProps;
}) => {
  const { updateCartItemsWithUpdateHandler } = useCartUpdate();
  const {
    onQuickAddButtonClick,
    onQuickAddSubmit,
    onChooseOptionClick,
    onChooseOptionQuantityUpdateSubmit,
  } = useContext(QuickAddTrackingEventsContext);
  const containerRef = useProductTileContainerRef();
  const currentContainerRef = useContainerRef(containerRef);

  const canQuickAddProduct = canQuickAdd(state, quickAdd);
  if (!canQuickAddProduct) {
    return null;
  }

  return (
    <div
      style={{ position: "absolute", right: "0px" }}
      data-test-id="composable-quick-add"
    >
      <Suspense fallback={null} key={`${productToken}-suspense`}>
        <LazyEncapsulatedQuickAdd
          key={`${productToken}-encapsulated-quick-add`}
          productToken={productToken}
          quickAdd={quickAdd}
          brandToken={brandToken}
          imageContainerRef={
            currentContainerRef ? { current: currentContainerRef } : undefined
          }
          updateItems={updateCartItemsWithUpdateHandler}
          onQuickAddClick={onQuickAddButtonClick}
          onQuickAddSubmit={onQuickAddSubmit}
          onChooseOption={onChooseOptionClick}
          onChooseOptionQuantityUpdate={onChooseOptionQuantityUpdateSubmit}
          showModalOnClick
        />
      </Suspense>
    </div>
  );
};

export const QuickAdd = ({
  data,
  showOnHover,
}: {
  data: QuickAddProps;
  showOnHover: boolean;
}) => {
  const shouldPersistQuickAdd = useSettingPersistQuickAddOnSearchTile();
  if (showOnHover && !shouldPersistQuickAdd) {
    return (
      <ComponentDisplayOnHoverContainer key={data.productToken ?? ""}>
        <QuickAddTrackingEventsContextProvider data={data}>
          <QuickAddBase data={data} />
        </QuickAddTrackingEventsContextProvider>
      </ComponentDisplayOnHoverContainer>
    );
  } else {
    return (
      <QuickAddTrackingEventsContextProvider data={data}>
        <QuickAddBase data={data} />
      </QuickAddTrackingEventsContextProvider>
    );
  }
};
